import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { companyService } from '../services/CompanyService';
import { storageService } from '../services/StorageService';
import { MileageRate } from '../services/MileageService';
import { billingCodeService, RuntimeBillingCode } from '../services/BillingCodeService';
import { Building2, Save, DollarSign, Plus, Trash2, FileText } from 'lucide-react';

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const { company, refreshCompany, updateCompanySettings } = useCompany();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(company?.logoUrl || null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Mileage reimbursement rate history (effective-dated).
  const [newRate, setNewRate] = useState('');
  const [newRateEffectiveDate, setNewRateEffectiveDate] = useState('');
  const [savingRate, setSavingRate] = useState(false);
  // Inline status for the mileage card (the page-level message is far up the page).
  const [rateMessage, setRateMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Billing codes (runtime-editable collection).
  const [billingCodes, setBillingCodes] = useState<RuntimeBillingCode[]>([]);
  const [newCode, setNewCode] = useState('');
  const [newCodeDesc, setNewCodeDesc] = useState('');
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingMessage, setBillingMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const loadBillingCodes = async (companyId: string) => {
    try {
      setBillingCodes(await billingCodeService.getByCompany(companyId));
    } catch (e) {
      console.error('Failed to load billing codes:', e);
    }
  };

  useEffect(() => {
    if (company?.id) loadBillingCodes(company.id);
  }, [company?.id]);

  const handleSeedBillingCodes = async () => {
    if (!company) return;
    setBillingBusy(true);
    setBillingMessage(null);
    try {
      const n = await billingCodeService.seedDefaults(company.id);
      await loadBillingCodes(company.id);
      setBillingMessage({ type: 'success', text: n > 0 ? `Seeded ${n} standard codes.` : 'Codes already exist.' });
    } catch (e) {
      const detail = e instanceof Error ? `: ${e.message}` : '';
      setBillingMessage({ type: 'error', text: `Failed to seed codes${detail}` });
    } finally {
      setBillingBusy(false);
    }
  };

  const handleAddBillingCode = async () => {
    if (!company) return;
    if (!newCode.trim() || !newCodeDesc.trim()) {
      setBillingMessage({ type: 'error', text: 'Enter a code and a description.' });
      return;
    }
    setBillingBusy(true);
    setBillingMessage(null);
    try {
      await billingCodeService.add(company.id, { code: newCode, description: newCodeDesc });
      setNewCode('');
      setNewCodeDesc('');
      await loadBillingCodes(company.id);
      setBillingMessage({ type: 'success', text: 'Billing code added.' });
    } catch (e) {
      const detail = e instanceof Error ? `: ${e.message}` : '';
      setBillingMessage({ type: 'error', text: `Failed to add code${detail}` });
    } finally {
      setBillingBusy(false);
    }
  };

  const handleToggleBillingCode = async (bc: RuntimeBillingCode) => {
    if (!company) return;
    setBillingBusy(true);
    setBillingMessage(null);
    try {
      await billingCodeService.setActive(bc.id, !bc.active);
      await loadBillingCodes(company.id);
    } catch (e) {
      const detail = e instanceof Error ? `: ${e.message}` : '';
      setBillingMessage({ type: 'error', text: `Failed to update code${detail}` });
    } finally {
      setBillingBusy(false);
    }
  };

  const [formData, setFormData] = useState({
    name: company?.name || '',
    email: company?.email || '',
    phone: company?.phone || '',
    address: company?.address || '',
  });

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !company) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 2MB' });
      return;
    }

    setUploadingLogo(true);
    setMessage(null);

    try {
      // Upload to storage
      const logoUrl = await storageService.uploadFile(
        file,
        `logos/${company.id}/${file.name}`
      );

      // Update company with logo URL
      await companyService.updateCompany(company.id, { logoUrl });
      await refreshCompany();

      setLogoPreview(logoUrl);
      setMessage({ type: 'success', text: 'Logo uploaded successfully!' });
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({ type: 'error', text: 'Failed to upload logo' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!company) return;

    setUploadingLogo(true);
    setMessage(null);

    try {
      // Delete old logo from storage if it exists
      if (company.logoUrl) {
        await storageService.deleteFile(company.logoUrl);
      }

      await companyService.updateCompany(company.id, { logoUrl: '' });
      await refreshCompany();
      setLogoPreview(null);
      setMessage({ type: 'success', text: 'Logo removed successfully!' });
    } catch (error) {
      console.error('Error removing logo:', error);
      setMessage({ type: 'error', text: 'Failed to remove logo' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;

    setLoading(true);
    setMessage(null);

    try {
      await companyService.updateCompany(company.id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
      });
      await refreshCompany();
      setMessage({ type: 'success', text: 'Company information updated successfully!' });
    } catch (error) {
      console.error('Error updating company:', error);
      setMessage({ type: 'error', text: 'Failed to update company information' });
    } finally {
      setLoading(false);
    }
  };

  // Rates sorted newest-effective-first for display.
  const mileageRates: MileageRate[] = [...(company?.settings?.mileageRates || [])].sort(
    (a, b) => b.effectiveDate.localeCompare(a.effectiveDate)
  );

  const handleAddRate = async () => {
    const rateValue = parseFloat(newRate);
    if (isNaN(rateValue) || rateValue <= 0) {
      setRateMessage({ type: 'error', text: 'Enter a rate greater than 0 (dollars per mile).' });
      return;
    }
    if (!newRateEffectiveDate) {
      setRateMessage({ type: 'error', text: 'Choose an effective date for this rate.' });
      return;
    }

    setSavingRate(true);
    setRateMessage(null);
    try {
      const existing = company?.settings?.mileageRates || [];
      // Replace any rate with the same effective date; otherwise append.
      const withoutSameDate = existing.filter((r) => r.effectiveDate !== newRateEffectiveDate);
      const updated: MileageRate[] = [
        ...withoutSameDate,
        { rate: rateValue, effectiveDate: newRateEffectiveDate },
      ];
      await updateCompanySettings({ mileageRates: updated });
      setNewRate('');
      setNewRateEffectiveDate('');
      setRateMessage({ type: 'success', text: `Rate $${rateValue.toFixed(3)}/mi effective ${newRateEffectiveDate} saved.` });
    } catch (error) {
      console.error('Error saving mileage rate:', error);
      const detail = error instanceof Error ? `: ${error.message}` : '';
      setRateMessage({ type: 'error', text: `Failed to save mileage rate${detail}` });
    } finally {
      setSavingRate(false);
    }
  };

  const handleRemoveRate = async (effectiveDate: string) => {
    setSavingRate(true);
    setRateMessage(null);
    try {
      const existing = company?.settings?.mileageRates || [];
      const updated = existing.filter((r) => r.effectiveDate !== effectiveDate);
      await updateCompanySettings({ mileageRates: updated });
      setRateMessage({ type: 'success', text: `Rate effective ${effectiveDate} removed.` });
    } catch (error) {
      console.error('Error removing mileage rate:', error);
      const detail = error instanceof Error ? `: ${error.message}` : '';
      setRateMessage({ type: 'error', text: `Failed to remove mileage rate${detail}` });
    } finally {
      setSavingRate(false);
    }
  };

  const handleToggleCommute = async (
    key: 'mileageReimburseFirstLeg' | 'mileageReimburseLastLeg',
    value: boolean
  ) => {
    setSavingRate(true);
    setRateMessage(null);
    try {
      await updateCompanySettings({ [key]: value });
      setRateMessage({ type: 'success', text: 'Commute policy updated.' });
    } catch (error) {
      console.error('Error updating commute policy:', error);
      const detail = error instanceof Error ? `: ${error.message}` : '';
      setRateMessage({ type: 'error', text: `Failed to update commute policy${detail}` });
    } finally {
      setSavingRate(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)' }}>
        <div className="alert alert-error">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        <h1 style={{ marginBottom: 'var(--spacing-2)' }}>Company Settings</h1>
        <p style={{ color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-8)' }}>
          Manage your company profile and branding
        </p>

        {message && (
          <div className={`alert alert-${message.type}`} style={{ marginBottom: 'var(--spacing-6)' }}>
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-2" style={{ gap: 'var(--spacing-6)' }}>
          {/* Company Logo Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Company Logo</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--spacing-4)' }}>
              {logoPreview ? (
                <div style={{ position: 'relative' }}>
                  <img
                    src={logoPreview}
                    alt="Company Logo"
                    style={{
                      width: '200px',
                      height: '200px',
                      objectFit: 'contain',
                      border: '2px solid var(--color-gray-200)',
                      borderRadius: 'var(--radius-lg)',
                      padding: 'var(--spacing-4)',
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: '200px',
                    height: '200px',
                    border: '2px dashed var(--color-gray-300)',
                    borderRadius: 'var(--radius-lg)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--spacing-2)',
                    color: 'var(--color-gray-400)',
                  }}
                >
                  <Building2 size={48} />
                  <p style={{ fontSize: 'var(--text-sm)' }}>No logo uploaded</p>
                </div>
              )}

              <div style={{ display: 'flex', gap: 'var(--spacing-3)', width: '100%', justifyContent: 'center' }}>
                <label htmlFor="logo-upload" className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                  style={{ display: 'none' }}
                />
                {logoPreview && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="btn btn-error"
                    disabled={uploadingLogo}
                  >
                    Remove Logo
                  </button>
                )}
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', textAlign: 'center' }}>
                Recommended: PNG or SVG, max 2MB
              </p>
            </div>
          </div>

          {/* Company Information Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Company Information</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Company Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ width: '100%' }}
              >
                <Save size={20} style={{ marginRight: 'var(--spacing-2)' }} />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>

        {/* Subscription Information */}
        <div className="card" style={{ marginTop: 'var(--spacing-6)' }}>
          <div className="card-header">
            <h2 className="card-title">Subscription Details</h2>
          </div>
          <div className="grid grid-cols-3" style={{ gap: 'var(--spacing-6)' }}>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-2)' }}>
                Current Plan
              </p>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)' }}>
                {company?.subscription?.plan?.toUpperCase() || 'N/A'}
              </p>
            </div>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-2)' }}>
                Storage Limit
              </p>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)' }}>
                {company?.subscription?.storageLimit || 0} GB
              </p>
            </div>
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-2)' }}>
                User Limit
              </p>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)' }}>
                {company?.subscription?.userLimit || 0} users
              </p>
            </div>
          </div>
        </div>

        {/* Mileage Reimbursement Rate */}
        <div className="card" style={{ marginTop: 'var(--spacing-6)' }}>
          <div className="card-header">
            <h2 className="card-title">
              <DollarSign size={20} style={{ marginRight: 'var(--spacing-2)', verticalAlign: 'text-bottom' }} />
              Mileage Reimbursement Rate
            </h2>
          </div>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-4)' }}>
            Set the reimbursement rate (dollars per mile) with an effective date. When the IRS
            rate changes, add a new rate with the new effective date rather than editing the old
            one — reimbursement uses the rate that was in effect on the date of travel. Distances
            are straight-line (GPS-to-GPS), not road miles.
          </p>

          {/* Existing rates */}
          {mileageRates.length === 0 ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', marginBottom: 'var(--spacing-4)' }}>
              No rates configured yet — a built-in IRS fallback rate is used until you add one.
            </p>
          ) : (
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              {mileageRates.map((r) => (
                <div
                  key={r.effectiveDate}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--spacing-3)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--radius-md, 6px)',
                    marginBottom: 'var(--spacing-2)',
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 'var(--font-semibold)' }}>${r.rate.toFixed(3)}/mi</span>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginLeft: 'var(--spacing-3)' }}>
                      effective {r.effectiveDate}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-error"
                    onClick={() => handleRemoveRate(r.effectiveDate)}
                    disabled={savingRate}
                    aria-label={`Remove rate effective ${r.effectiveDate}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {rateMessage && (
            <div className={`alert alert-${rateMessage.type}`} style={{ marginBottom: 'var(--spacing-4)' }}>
              {rateMessage.text}
            </div>
          )}

          {/* Add a rate */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-4)', alignItems: 'flex-end' }}>
            <div>
              <label className="form-label">Rate ($/mile)</label>
              <input
                type="number"
                step="0.001"
                min="0"
                className="form-input"
                placeholder="0.670"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Effective date</label>
              <input
                type="date"
                className="form-input"
                value={newRateEffectiveDate}
                onChange={(e) => setNewRateEffectiveDate(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddRate}
              disabled={savingRate}
            >
              <Plus size={18} style={{ marginRight: 'var(--spacing-2)' }} />
              {savingRate ? 'Saving...' : 'Add Rate'}
            </button>
          </div>

          {/* Commute policy — which daily legs are reimbursable */}
          <div style={{ borderTop: '1px solid var(--color-gray-200)', marginTop: 'var(--spacing-6)', paddingTop: 'var(--spacing-4)' }}>
            <h3 style={{ marginBottom: 'var(--spacing-2)' }}>Commute Policy</h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-3)' }}>
              Between-client trips are always reimbursable. By default the daily commute legs —
              home to the first client, and the last client back home — are NOT reimbursed. Enable
              these only if your policy reimburses them.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!company?.settings?.mileageReimburseFirstLeg}
                disabled={savingRate}
                onChange={(e) => handleToggleCommute('mileageReimburseFirstLeg', e.target.checked)}
              />
              Reimburse first leg (home → first client)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!company?.settings?.mileageReimburseLastLeg}
                disabled={savingRate}
                onChange={(e) => handleToggleCommute('mileageReimburseLastLeg', e.target.checked)}
              />
              Reimburse last leg (last client → home)
            </label>
          </div>
        </div>

        {/* Billing Codes */}
        <div className="card" style={{ marginTop: 'var(--spacing-6)' }}>
          <div className="card-header">
            <h2 className="card-title">
              <FileText size={20} style={{ marginRight: 'var(--spacing-2)', verticalAlign: 'text-bottom' }} />
              Billing Codes
            </h2>
          </div>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-4)' }}>
            The CPT codes available in the app. Add your own or deactivate ones you don't use.
          </p>

          {billingMessage && (
            <div className={`alert alert-${billingMessage.type}`} style={{ marginBottom: 'var(--spacing-4)' }}>
              {billingMessage.text}
            </div>
          )}

          {billingCodes.length === 0 ? (
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', marginBottom: 'var(--spacing-3)' }}>
                No codes configured yet. Seed the standard ABA CPT codes to get started.
              </p>
              <button type="button" className="btn btn-primary" onClick={handleSeedBillingCodes} disabled={billingBusy}>
                {billingBusy ? 'Seeding...' : 'Seed standard codes'}
              </button>
            </div>
          ) : (
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              {billingCodes.map((bc) => (
                <div
                  key={bc.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--spacing-3)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--radius-md, 6px)',
                    marginBottom: 'var(--spacing-2)',
                    opacity: bc.active ? 1 : 0.55,
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 'var(--font-semibold)' }}>{bc.code}</span>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginLeft: 'var(--spacing-3)' }}>
                      {bc.description}
                    </span>
                    {!bc.active && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)', marginLeft: 'var(--spacing-2)' }}>
                        (inactive)
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className={`btn ${bc.active ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={() => handleToggleBillingCode(bc)}
                    disabled={billingBusy}
                  >
                    {bc.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add a code */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-4)', alignItems: 'flex-end' }}>
            <div>
              <label className="form-label">Code</label>
              <input
                type="text"
                className="form-input"
                placeholder="97153"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
              />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label className="form-label">Description</label>
              <input
                type="text"
                className="form-input"
                placeholder="Adaptive behavior treatment by protocol"
                value={newCodeDesc}
                onChange={(e) => setNewCodeDesc(e.target.value)}
              />
            </div>
            <button type="button" className="btn btn-primary" onClick={handleAddBillingCode} disabled={billingBusy}>
              <Plus size={18} style={{ marginRight: 'var(--spacing-2)' }} />
              {billingBusy ? 'Saving...' : 'Add Code'}
            </button>
          </div>
        </div>
    </div>
  );
};
